import { RegionShared } from "@1games-ru/region-shared";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider";

type Handler = (keyCode: number) => void;

interface KeyPressContextType {
  subscribeKeyDown: (key: string, handler: Handler) => void;
  subscribeKeyUp: (key: string, handler: Handler) => void;
  unsubscribeKeyDown: (key: string) => void;
  unsubscribeKeyUp: (key: string) => void;
}

const KeyPressContext = createContext<KeyPressContextType | undefined>(undefined);

interface KeyPressProviderProps {
  children: ReactNode;
}

export function KeyPressProvider({ children }: KeyPressProviderProps) {
  const [keyDownHandlers, setKeyDownHandlers] = useState<Map<string, Handler>>(new Map());
  const [keyUpHandlers, setKeyUpHandlers] = useState<Map<string, Handler>>(new Map());

  const subscribeKeyDown = useCallback((key: string, handler: Handler) => {
    setKeyDownHandlers((state) => {
      const newState = new Map(state);
      newState.set(key, handler);
      return newState;
    });
  }, []);

  const unsubscribeKeyDown = useCallback((key: string) => {
    setKeyDownHandlers((state) => {
      const newState = new Map(state);
      newState.delete(key);
      return newState;
    });
  }, []);

  const subscribeKeyUp = useCallback((key: string, handler: Handler) => {
    setKeyUpHandlers((state) => {
      const newState = new Map(state);
      newState.set(key, handler);
      return newState;
    });
  }, []);

  const unsubscribeKeyUp = useCallback((key: string) => {
    setKeyUpHandlers((state) => {
      const newState = new Map(state);
      newState.delete(key);
      return newState;
    });
  }, []);

  useEffect(() => {
    RpcMpProvider.getInstance().subscribe(
      RegionShared.Cef.Events.Key.KeyDown,
      (request: typeof RegionShared.Cef.Requests.KeyDownRequest) => {
        Array.from(keyDownHandlers.entries()).map(([_, handler]) => handler(request.keyCode));
      },
    );

    RpcMpProvider.getInstance().subscribe(
      RegionShared.Cef.Events.Key.KeyUp,
      (request: typeof RegionShared.Cef.Requests.KeyUpRequest) => {
        Array.from(keyUpHandlers.entries()).map(([_, handler]) => handler(request.keyCode));
      },
    );
  }, [keyDownHandlers, keyUpHandlers]);

  const value = useMemo(
    () => ({
      subscribeKeyDown,
      subscribeKeyUp,
      unsubscribeKeyDown,
      unsubscribeKeyUp,
    }),
    [subscribeKeyDown, subscribeKeyUp, unsubscribeKeyDown, unsubscribeKeyUp],
  );

  return <KeyPressContext.Provider value={value}>{children}</KeyPressContext.Provider>;
}

export const useKeyPress = () => {
  const context = useContext(KeyPressContext);
  if (context === undefined) {
    throw new Error("useKeyPress must be used within a KeyPressProvider");
  }
  return context;
};
